import { useMemo, type ReactNode } from "react";
import { signOutLocal } from "@common/lib/signOut";
import { useGroupSession } from "@/state/hooks/useGroupSession";
import HDTabs from "@/common/components/HDTabs";
import type { HDTab } from "@common/utils/types";

/**
 * Minimal authenticated shell for /corporate. Deliberately does NOT mount
 * the clinic Navbar or any of its dependents (RecordingWidget, ScoutChat,
 * AssetLibraryProvider, TourProvider) — group admins viewing cross-clinic
 * usage have no recording/document context to carry.
 *
 * The header mirrors the clinic Navbar's layout (logo · name · tabs) so the
 * corporate view feels like the same app: the account group name stands in for
 * the clinic name, and a single always-active "Usage" tab stands in for the
 * clinic's Dashboard/Visits tabs.
 */
export default function CorporateLayout({ children }: { children: ReactNode }) {
    const { data: groupMe } = useGroupSession();
    const groupName = groupMe?.groupName ?? "Corporate";

    // One tab today (Usage). Kept as an HDTabs list so it matches the clinic
    // navbar's tab styling and can grow if the corporate view gains more views.
    const tabs = useMemo<HDTab[]>(() => [{ header: "Usage", id: "/corporate", isActive: true }], []);

    return (
        <div className="flex flex-col min-h-screen bg-gray-100">
            <div className="flex h-16 shrink-0 items-center border-b border-gray-200 bg-gray-100 px-4">
                <div className="flex flex-1 items-center gap-x-5 self-stretch">
                    <img src="/happydoc.svg" alt="HappyDoc logo" className="hidden sm:inline h-8 w-auto" />
                    <img src="/favicon.png" alt="HappyDoc logo" className="sm:hidden h-8 w-auto" />
                    <h1 className="hidden lg:inline-flex text-xl font-semibold leading-6 text-gray-900">{groupName}</h1>
                    <div>
                        {/* changeTab is a no-op: the single tab is already the current view. */}
                        <HDTabs tabs={tabs} changeTab={() => {}} paddingVertical="py-5 sm:py-[18px]" />
                    </div>
                </div>
                <div className="ml-auto flex items-center gap-x-2">
                    <button
                        type="button"
                        // Hard-navigate to /login rather than letting the SPA's onAuthStateChange
                        // handler swap routers in place. That in-place swap (app.tsx recreates the
                        // router on every auth change) can leave a stale route mounted — signing out
                        // was landing on the signup page instead of login. A full document load
                        // guarantees a clean unauthenticated boot straight to the sign-in screen.
                        // `.finally` so we still navigate even if the sign-out call errors.
                        onClick={() => void signOutLocal().finally(() => window.location.assign("/login"))}
                        className="focused text-sm font-semibold text-gray-700 hover:text-gray-900 rounded-lg px-3 py-2 hover:bg-gray-200/80 focus:outline-none focus:ring-2 focus:ring-max-400 focus:ring-offset-1"
                    >
                        Sign out
                    </button>
                </div>
            </div>
            <main id="main-content" className="flex-1">
                {children}
            </main>
        </div>
    );
}
